import React, { useRef } from 'react';
import type { SortDirection } from './useTablePreferences';

export type ResizableHeaderCellProps = {
  children?: React.ReactNode;
  width: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
  sortable?: boolean;
  sortDirection?: SortDirection | null;
  onSort?: () => void;
  onResize: (width: number) => void;
  onReset?: () => void;
};

export default function ResizableHeaderCell({
  children,
  width,
  minWidth = 80,
  maxWidth = 600,
  resizable = true,
  align = 'left',
  className = '',
  sortable = false,
  sortDirection = null,
  onSort,
  onResize,
  onReset,
}: ResizableHeaderCellProps) {
  const startX = useRef(0);
  const startWidth = useRef(width);

  function onMouseDown(event: React.MouseEvent) {
    if (!resizable) return;
    event.preventDefault();
    event.stopPropagation();
    startX.current = event.clientX;
    startWidth.current = width;

    function onMouseMove(moveEvent: MouseEvent) {
      const next = Math.min(
        maxWidth,
        Math.max(minWidth, startWidth.current + moveEvent.clientX - startX.current)
      );
      onResize(next);
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.classList.remove('col-resize-active');
    }

    document.body.classList.add('col-resize-active');
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  function onDoubleClick(event: React.MouseEvent) {
    if (!resizable || !onReset) return;
    event.preventDefault();
    event.stopPropagation();
    onReset();
  }

  function onLabelClick(event: React.MouseEvent) {
    if (!sortable || !onSort) return;
    event.stopPropagation();
    onSort();
  }

  function onLabelKeyDown(event: React.KeyboardEvent) {
    if (!sortable || !onSort) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSort();
    }
  }

  const thClass = [
    'data-table__th',
    'admin-table-th',
    !resizable && 'admin-table-th-fixed',
    align === 'right' ? 'data-table__th--right' : '',
    align === 'center' ? 'data-table__th--center' : '',
    sortable ? 'data-table__th--sortable' : '',
    sortDirection ? `data-table__th--sorted-${sortDirection}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const sortIndicator =
    sortDirection === 'asc' ? '▲' : sortDirection === 'desc' ? '▼' : sortable ? '⇅' : '';

  return (
    <th
      className={thClass}
      style={{
        width,
        minWidth,
        maxWidth: resizable ? maxWidth : width,
      }}
    >
      <span
        className="admin-table-th-label"
        onClick={sortable ? onLabelClick : undefined}
        onKeyDown={sortable ? onLabelKeyDown : undefined}
        role={sortable ? 'button' : undefined}
        tabIndex={sortable ? 0 : undefined}
      >
        {children}
        {sortIndicator ? (
          <span className="data-table__sort-indicator" aria-hidden="true">
            {sortIndicator.trim()}
          </span>
        ) : null}
      </span>
      {resizable && (
        <span
          className="data-table__resize-handle col-resize-handle"
          onMouseDown={onMouseDown}
          onDoubleClick={onDoubleClick}
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${typeof children === 'string' ? children : 'column'}`}
        />
      )}
    </th>
  );
}
